import React from "react";
import ReactDOM from "react-dom/client";
import { Building2, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, FileText, Filter, List, LogOut, Map as MapIcon, MapPin, Pencil, Phone, Mail, Search, Users, XCircle } from "lucide-react";
import { MapView } from "./MapView";
import { NucleosMiniMap } from "./NucleosMiniMap";
import { generateRegionReport } from "./generateReport";
import type { Municipality, Nucleo, Region } from "./types";
import "./styles.css";

type User = {
  email: string;
  name: string;
  picture?: string;
  exp: number;
};

type Tab = "list" | "map";
type RegionPanelView = "nucleos" | "cidades";

type BootstrapPayload = {
  user?: User;
  canEdit?: boolean;
  regions: Region[];
  municipalities: Municipality[];
  nucleos: Nucleo[];
  generatedAt: string;
};

type Toast = {
  kind: "success" | "error";
  message: string;
};

// Cliente do site (cadastro) que segue a regiao da cidade.
type Customer = {
  id: number;
  nome: string;
  cidade: string;
  estado: string;
  status: string;
  email: string;
  celular: string;
  regiao: number | null;
};

type CustomersPayload = {
  available: boolean;
  can_apply: boolean;
  city: { ibge_code: string; name: string; uf: string; region_id: number };
  customers: Customer[];
  pending: number;
};

// Popup aberto depois de reatribuir uma cidade que tem clientes cadastrados.
type CustomerPrompt = {
  city: Municipality;
  region: Region;
  customers: Customer[];
  pending: number;
  canApply: boolean;
};

type StoredGeographicRegionFilter = {
  value: string[];
  expiresAt: number;
};

const GEOGRAPHIC_REGION_OPTIONS = ["Norte", "Nordeste", "Centro-Oeste", "Sudeste", "Sul"];
const GEOGRAPHIC_REGION_FILTER_STORAGE_KEY = "sr-regioes:selected-geographic-regions";
const GEOGRAPHIC_REGION_FILTER_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const ERROR_MESSAGES: Record<string, string> = {
  auth_failed: "Autenticacao falhou. Tente novamente.",
  token_failed: "Erro ao obter token. Tente novamente.",
  not_allowed: "Este email nao esta autorizado a acessar o painel.",
  session_expired: "Sua sessao expirou. Faca login novamente.",
};

function App() {
  const [loading, setLoading] = React.useState(true);
  const [user, setUser] = React.useState<User | null>(null);
  const [canEdit, setCanEdit] = React.useState(false);
  const [regions, setRegions] = React.useState<Region[]>([]);
  const [municipalities, setMunicipalities] = React.useState<Municipality[]>([]);
  const [nucleos, setNucleos] = React.useState<Nucleo[]>([]);
  const [selectedRegionId, setSelectedRegionId] = React.useState<number | null>(null);
  const [regionPanelView, setRegionPanelView] = React.useState<RegionPanelView>("nucleos");
  const [editingNucleo, setEditingNucleo] = React.useState<Nucleo | null>(null);
  const [selectedGeographicRegions, setSelectedGeographicRegions] = React.useState<string[]>(readStoredGeographicRegions);
  const [tab, setTab] = React.useState<Tab>("list");
  const [query, setQuery] = React.useState("");
  const [saving, setSaving] = React.useState<Record<string, boolean>>({});
  const [toast, setToast] = React.useState<Toast | null>(null);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [customerPrompt, setCustomerPrompt] = React.useState<CustomerPrompt | null>(null);
  const loginError = getLoginError();

  React.useEffect(() => {
    void loadBootstrap();
  }, []);

  React.useEffect(() => {
    storeGeographicRegions(selectedGeographicRegions);
  }, [selectedGeographicRegions]);

  async function loadBootstrap() {
    setLoading(true);
    try {
      const res = await fetch("/api/bootstrap", { credentials: "include" });
      if (res.status === 401) {
        setUser(null);
        return;
      }
      if (!res.ok) throw new Error("Falha ao carregar dados");
      const data = (await res.json()) as BootstrapPayload;
      setUser(data.user || null);
      setCanEdit(data.canEdit === true);
      setRegions(data.regions);
      setMunicipalities(data.municipalities);
      setNucleos(data.nucleos || []);
    } catch {
      showToast("error", "Nao foi possivel carregar a lista.");
    } finally {
      setLoading(false);
    }
  }

  async function updateRegion(city: Municipality, regionId: number) {
    if (!canEdit) return;
    if (city.region_id === regionId) return;

    const previous = city.region_id;
    setSaving((state) => ({ ...state, [city.ibge_code]: true }));
    setMunicipalities((items) =>
      items.map((item) =>
        item.ibge_code === city.ibge_code ? { ...item, region_id: regionId, assignment_source: "manual" } : item,
      ),
    );

    try {
      const res = await fetch(`/api/municipalities/${city.ibge_code}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ region_id: regionId }),
      });
      if (!res.ok) throw new Error("Falha ao salvar");
      const payload = (await res.json()) as { municipality: Municipality };
      setMunicipalities((items) =>
        items.map((item) => (item.ibge_code === city.ibge_code ? payload.municipality : item)),
      );
      showToast("success", `${city.name} atualizada.`);
      // A cidade mudou de regiao; os clientes do site cadastrados nela continuam
      // com o responsavel antigo ate o usuario confirmar no popup.
      void promptCustomers(payload.municipality || { ...city, region_id: regionId }, regionId);
    } catch {
      setMunicipalities((items) =>
        items.map((item) => (item.ibge_code === city.ibge_code ? { ...item, region_id: previous } : item)),
      );
      showToast("error", `Nao foi possivel salvar ${city.name}.`);
    } finally {
      setSaving((state) => ({ ...state, [city.ibge_code]: false }));
    }
  }

  // Busca os clientes da cidade e abre o popup quando algum deles ainda esta em
  // outra regiao. Silencioso quando nao ha cliente ou o banco do site nao responde:
  // a reatribuicao da cidade em si ja foi salva.
  async function promptCustomers(city: Municipality, regionId: number) {
    const region = regions.find((item) => item.id === regionId);
    if (!region) return;
    try {
      const res = await fetch(`/api/municipalities/${city.ibge_code}/customers`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const data = (await res.json()) as CustomersPayload;
      if (!data.available || data.pending < 1) return;
      setCustomerPrompt({
        city,
        region,
        customers: data.customers,
        pending: data.pending,
        canApply: data.can_apply === true,
      });
    } catch {
      /* sem popup: a cidade ja foi salva */
    }
  }

  async function applyCustomerRegion(prompt: CustomerPrompt) {
    const res = await fetch(`/api/municipalities/${prompt.city.ibge_code}/customers`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    const payload = (await res.json().catch(() => null)) as { updated?: number; error?: string } | null;
    if (!res.ok) throw new Error(payload?.error || "Falha ao atualizar os clientes");
    return payload?.updated || 0;
  }

  async function saveNucleo(id: number, changes: Partial<Nucleo>) {
    const res = await fetch(`/api/nucleos/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changes),
    });
    if (!res.ok) throw new Error("Falha ao salvar");
    const payload = (await res.json()) as { nucleo: Nucleo };
    setNucleos((items) => items.map((item) => (item.id === id ? payload.nucleo : item)));
    return payload.nucleo;
  }

  function showToast(kind: Toast["kind"], message: string) {
    setToast({ kind, message });
    window.setTimeout(() => setToast(null), 3200);
  }

  const filtered = React.useMemo(() => {
    const term = normalize(query);
    const selected = new Set(selectedGeographicRegions);
    return municipalities.filter((city) => {
      const geographicRegionOk = selected.has(city.geographic_region);
      const region = regions.find((item) => item.id === city.region_id);
      const queryOk =
        !term ||
        normalize(city.name).includes(term) ||
        normalize(city.uf).includes(term) ||
        normalize(city.state_name).includes(term) ||
        normalize(formatRegionName(region)).includes(term);
      return geographicRegionOk && queryOk;
    });
  }, [municipalities, query, regions, selectedGeographicRegions]);

  const grouped = React.useMemo(() => groupByState(filtered), [filtered]);
  const counts = React.useMemo(() => countByRegion(filtered, regions), [filtered, regions]);
  const nucleoCounts = React.useMemo(() => {
    const map: Record<number, number> = {};
    for (const item of regions) map[item.id] = 0;
    for (const nucleo of nucleos) {
      if (nucleo.region_id != null) map[nucleo.region_id] = (map[nucleo.region_id] || 0) + 1;
    }
    return map;
  }, [nucleos, regions]);
  const selectedNucleos = React.useMemo(
    () =>
      selectedRegionId == null
        ? []
        : nucleos
            .filter((item) => item.region_id === selectedRegionId)
            .sort((a, b) => a.uf.localeCompare(b.uf, "pt-BR") || a.cidade.localeCompare(b.cidade, "pt-BR")),
    [nucleos, selectedRegionId],
  );
  // Mesma barra de busca filtra os nucleos quando a view de nucleos esta aberta.
  const filteredNucleos = React.useMemo(() => {
    const term = normalize(query.trim());
    if (!term) return selectedNucleos;
    return selectedNucleos.filter((n) =>
      [n.nome, n.cidade, n.uf, n.bairro, n.endereco, n.presidente_nome, n.secretario_nome, n.email].some(
        (value) => value && normalize(value).includes(term),
      ),
    );
  }, [selectedNucleos, query]);
  const selectedCities = React.useMemo(
    () =>
      selectedRegionId == null
        ? []
        : filtered
            .filter((item) => item.region_id === selectedRegionId)
            .sort((a, b) => a.uf.localeCompare(b.uf, "pt-BR") || a.name.localeCompare(b.name, "pt-BR")),
    [filtered, selectedRegionId],
  );
  const selectedRegion = regions.find((item) => item.id === selectedRegionId) || null;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Carregando regiões...</p>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen error={loginError} />;
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <strong>Sul Remates</strong>
          <span>Regiões comerciais</span>
          {!canEdit ? <span className="readonly-badge">Somente leitura</span> : null}
        </div>

        <section className="summary" hidden={tab === "map"}>
          <span>
            {filtered.length.toLocaleString("pt-BR")} cidades
            {selectedGeographicRegions.length < GEOGRAPHIC_REGION_OPTIONS.length
              ? ` (${formatGeographicRegions(selectedGeographicRegions)})`
              : ""}
          </span>
          {regions.map((item) => {
            const active = selectedRegionId === item.id;
            return (
              <button
                type="button"
                className={active ? "summary-row summary-row--active" : "summary-row"}
                key={item.id}
                onClick={() => setSelectedRegionId(active ? null : item.id)}
                title={`Ver ${nucleoCounts[item.id] || 0} núcleos da ${item.name}`}
              >
                <i style={{ background: item.color_hex }} />
                <span>{formatRegionName(item)}</span>
                <strong>{counts[item.id] || 0}</strong>
              </button>
            );
          })}
        </section>

        <div className="sidebar-map" hidden={tab === "map"}>
          <NucleosMiniMap
            municipalities={municipalities}
            regions={regions}
            selectedRegionId={selectedRegionId}
            onSelectRegion={(id) => setSelectedRegionId((prev) => (prev === id ? null : id))}
          />
          <small className="sidebar-map__hint">
            {selectedRegionId == null
              ? "Clique numa região acima para destacar seus núcleos."
              : `${selectedNucleos.length} núcleo${selectedNucleos.length === 1 ? "" : "s"} em destaque`}
          </small>
        </div>

        <button
          className="report-btn"
          type="button"
          onClick={() => setReportOpen(true)}
          title="Gerar relatório PDF com todas as cidades por região"
        >
          <FileText size={18} />
          Gerar Relatório PDF
        </button>

        <a className="logout" href="/api/auth/logout">
          <LogOut size={18} />
          Sair
        </a>
      </aside>

      <section className={tab === "map" ? "content content--map" : "content"}>
        <header className="topbar">
          <div>
            <p className="eyebrow">Mapa computavel</p>
            <h1>Regiões por cidade</h1>
          </div>
          <div className="topbar-actions">
            <nav className="tabs">
              <button
                className={tab === "list" ? "is-active" : ""}
                onClick={() => {
                  setTab("list");
                  setSelectedRegionId(null);
                }}
              >
                <List size={16} />
                Lista
              </button>
              <button
                className={tab === "map" ? "is-active" : ""}
                onClick={() => {
                  setTab("map");
                  setSelectedRegionId(null);
                }}
              >
                <MapIcon size={16} />
                Mapa
              </button>
            </nav>
            {tab === "list" ? (
              <div className="filters">
                <label className="search-box">
                  <Search size={18} />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Buscar cidade, UF ou região"
                  />
                </label>
                <GeographicRegionFilter
                  selected={selectedGeographicRegions}
                  onChange={setSelectedGeographicRegions}
                />
              </div>
            ) : null}
          </div>
        </header>

        {selectedRegionId != null && selectedRegion ? (
          <NucleosPanel
            region={selectedRegion}
            nucleos={filteredNucleos}
            cities={selectedCities}
            view={regionPanelView}
            onViewChange={setRegionPanelView}
            searching={normalize(query.trim()).length > 0}
            canEdit={canEdit}
            onEdit={setEditingNucleo}
            onClose={() => setSelectedRegionId(null)}
          />
        ) : null}

        {tab === "map" && selectedRegionId == null ? (
          <MapView regions={regions} municipalities={municipalities} />
        ) : null}

        <div className="state-list" hidden={tab !== "list" || selectedRegionId != null}>
          {grouped.map(([uf, cities]) => (
            <section className="state-section" key={uf}>
              <div className="state-heading">
                <div>
                  <h2>{uf}</h2>
                  <span>{cities[0]?.state_name}</span>
                </div>
                <strong>{cities.length.toLocaleString("pt-BR")}</strong>
              </div>
              <div className="city-table">
                {cities.map((city) => {
                  const region = regions.find((item) => item.id === city.region_id);
                  return (
                    <div className="city-row" key={city.ibge_code}>
                      <div className="city-name">
                        <MapPin size={16} />
                        <span>{city.name}</span>
                      </div>
                      <div className="region-cell">
                        <span className="region-dot" style={{ background: region?.color_hex || "#64748b" }} />
                        <select
                          value={city.region_id}
                          disabled={saving[city.ibge_code] || !canEdit}
                          onChange={(event) => updateRegion(city, Number(event.target.value))}
                        >
                          {regions.map((item) => (
                            <option key={item.id} value={item.id}>
                              {formatRegionName(item)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </section>

      {reportOpen ? (
        <ReportModal
          regions={regions}
          municipalities={municipalities}
          nucleos={nucleos}
          onClose={() => setReportOpen(false)}
        />
      ) : null}

      {editingNucleo ? (
        <NucleoEditModal
          nucleo={editingNucleo}
          municipalities={municipalities}
          onClose={() => setEditingNucleo(null)}
          onSave={async (changes) => {
            try {
              await saveNucleo(editingNucleo.id, changes);
              showToast("success", `${editingNucleo.nome} atualizado.`);
              setEditingNucleo(null);
            } catch {
              showToast("error", "Não foi possível salvar o núcleo.");
            }
          }}
        />
      ) : null}

      {customerPrompt ? (
        <CustomerRegionModal
          prompt={customerPrompt}
          regions={regions}
          onClose={() => setCustomerPrompt(null)}
          onConfirm={async () => {
            try {
              const updated = await applyCustomerRegion(customerPrompt);
              showToast(
                "success",
                `${updated} ${updated === 1 ? "cliente movido" : "clientes movidos"} para ${formatRegionName(customerPrompt.region)}.`,
              );
              setCustomerPrompt(null);
            } catch (err) {
              showToast("error", (err as Error)?.message || "Não foi possível atualizar os clientes.");
            }
          }}
        />
      ) : null}

      {toast ? (
        <div className={`toast toast--${toast.kind}`}>
          {toast.kind === "success" ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
          {toast.message}
        </div>
      ) : null}
    </main>
  );
}

/**
 * Popup pos-reatribuicao: lista os clientes do site que moram na cidade e ainda
 * apontam para outro responsavel, e oferece mover todos de uma vez. Cancelar
 * mantem a cidade na regiao nova e os clientes como estao.
 */
function CustomerRegionModal({
  prompt,
  regions,
  onClose,
  onConfirm,
}: {
  prompt: CustomerPrompt;
  regions: Region[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [saving, setSaving] = React.useState(false);
  const { city, region, customers, pending } = prompt;

  async function handleConfirm() {
    setSaving(true);
    try {
      await onConfirm();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="report-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="report-modal customers-modal">
        <header className="report-modal__header">
          <div>
            <h2>
              Deseja mudar a região do cadastro para {pending} {pending === 1 ? "cliente" : "clientes"}?
            </h2>
            <p>
              {city.name}/{city.uf} agora é <strong>{formatRegionName(region)}</strong>. Os clientes abaixo
              continuam com o responsável antigo.
            </p>
          </div>
          <button className="report-modal__close" onClick={onClose} type="button" disabled={saving}>
            <XCircle size={22} />
          </button>
        </header>

        <div className="report-modal__body">
          {customers.map((customer) => {
            const from = regions.find((item) => item.id === customer.regiao) || null;
            const unchanged = customer.regiao === region.id;
            return (
              <div className={unchanged ? "customer-row customer-row--done" : "customer-row"} key={customer.id}>
                <div className="customer-row__main">
                  <strong>{customer.nome || "(sem nome)"}</strong>
                  <small>
                    {[customer.email, customer.celular].filter(Boolean).join(" · ") || "sem contato"}
                  </small>
                </div>
                <div className="customer-row__regions">
                  <span className={`customer-status customer-status--${customer.status || "novo"}`}>
                    {customer.status || "—"}
                  </span>
                  {unchanged ? (
                    <span className="customer-row__same">já em {region.name}</span>
                  ) : (
                    <span className="customer-row__move">
                      <i style={{ background: from?.color_hex || "#64748b" }} />
                      {from ? from.name : "sem região"}
                      <ChevronRight size={13} />
                      <i style={{ background: region.color_hex }} />
                      {region.name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <footer className="report-modal__footer">
          <span className="report-modal__summary">
            {prompt.canApply ? (
              <>
                <Users size={14} /> {customers.length} {customers.length === 1 ? "cliente" : "clientes"} em{" "}
                {city.name}
              </>
            ) : (
              "Integração com o site indisponível (SITE_API_TOKEN)."
            )}
          </span>
          <div className="report-modal__actions">
            <button className="report-modal__cancel" type="button" onClick={onClose} disabled={saving}>
              Agora não
            </button>
            <button
              className="report-modal__generate"
              type="button"
              onClick={handleConfirm}
              disabled={saving || !prompt.canApply}
            >
              <Check size={16} />
              {saving ? "Movendo..." : `Mudar ${pending} ${pending === 1 ? "cliente" : "clientes"}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ReportModal({
  regions,
  municipalities,
  nucleos,
  onClose,
}: {
  regions: Region[];
  municipalities: Municipality[];
  nucleos: Nucleo[];
  onClose: () => void;
}) {
  const sorted = React.useMemo(
    () => [...regions].sort((a, b) => a.sort_order - b.sort_order),
    [regions],
  );
  const [selected, setSelected] = React.useState<Set<number>>(() => new Set(sorted.map((r) => r.id)));

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === sorted.length) setSelected(new Set());
    else setSelected(new Set(sorted.map((r) => r.id)));
  }

  function handleGenerate() {
    const filteredRegions = sorted.filter((r) => selected.has(r.id));
    const filteredMunicipalities = municipalities.filter((m) => selected.has(m.region_id));
    const filteredNucleos = nucleos.filter((n) => n.region_id != null && selected.has(n.region_id));
    generateRegionReport(filteredRegions, filteredMunicipalities, filteredNucleos);
    onClose();
  }

  const totalCities = municipalities.filter((m) => selected.has(m.region_id)).length;

  return (
    <div className="report-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="report-modal">
        <header className="report-modal__header">
          <div>
            <h2>Gerar Relatório PDF</h2>
            <p>Selecione as regiões para incluir no relatório</p>
          </div>
          <button className="report-modal__close" onClick={onClose} type="button">
            <XCircle size={22} />
          </button>
        </header>

        <div className="report-modal__body">
          <label className="report-modal__option report-modal__option--all">
            <input
              type="checkbox"
              checked={selected.size === sorted.length}
              onChange={toggleAll}
            />
            <span className="region-filter__checkbox">
              {selected.size === sorted.length ? <Check size={14} /> : null}
            </span>
            <span>Todas as regiões</span>
            <strong>{municipalities.length}</strong>
          </label>

          <div className="report-modal__divider" />

          {sorted.map((region) => {
            const checked = selected.has(region.id);
            const count = municipalities.filter((m) => m.region_id === region.id).length;
            return (
              <label className="report-modal__option" key={region.id}>
                <input type="checkbox" checked={checked} onChange={() => toggle(region.id)} />
                <span className="region-filter__checkbox">
                  {checked ? <Check size={14} /> : null}
                </span>
                <i className="report-modal__dot" style={{ background: region.color_hex }} />
                <span className="report-modal__label">
                  <strong>{region.name}</strong>
                  <small>{region.current_responsible_name}</small>
                </span>
                <strong className="report-modal__count">{count}</strong>
              </label>
            );
          })}
        </div>

        <footer className="report-modal__footer">
          <span className="report-modal__summary">
            {selected.size} {selected.size === 1 ? "região" : "regiões"} · {totalCities.toLocaleString("pt-BR")} cidades
          </span>
          <div className="report-modal__actions">
            <button className="report-modal__cancel" type="button" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="report-modal__generate"
              type="button"
              disabled={selected.size === 0}
              onClick={handleGenerate}
            >
              <FileText size={16} />
              Gerar PDF
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function NucleosPanel({
  region,
  nucleos,
  cities,
  view,
  onViewChange,
  searching,
  canEdit,
  onEdit,
  onClose,
}: {
  region: Region;
  nucleos: Nucleo[];
  cities: Municipality[];
  view: RegionPanelView;
  onViewChange: (view: RegionPanelView) => void;
  searching: boolean;
  canEdit: boolean;
  onEdit: (nucleo: Nucleo) => void;
  onClose: () => void;
}) {
  const groupedNucleos = React.useMemo(() => {
    const map = new Map<string, Nucleo[]>();
    for (const nucleo of nucleos) {
      const key = `${nucleo.cidade}/${nucleo.uf}`;
      const list = map.get(key) || [];
      list.push(nucleo);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [nucleos]);

  const groupedCities = React.useMemo(() => {
    const map = new Map<string, Municipality[]>();
    for (const city of cities) {
      const list = map.get(city.uf) || [];
      list.push(city);
      map.set(city.uf, list);
    }
    return Array.from(map.entries());
  }, [cities]);

  const count = view === "nucleos" ? nucleos.length : cities.length;

  return (
    <div className="nucleos-panel">
      <div className="nucleos-panel__head">
        <div className="nucleos-panel__head-row">
          <button className="nucleos-panel__back" type="button" onClick={onClose}>
            <ChevronLeft size={16} />
            Voltar
          </button>
          <nav className="tabs nucleos-panel__tabs">
            <button
              type="button"
              className={view === "nucleos" ? "is-active" : ""}
              onClick={() => onViewChange("nucleos")}
            >
              <Building2 size={16} />
              Núcleos
            </button>
            <button
              type="button"
              className={view === "cidades" ? "is-active" : ""}
              onClick={() => onViewChange("cidades")}
            >
              <MapPin size={16} />
              Cidades
            </button>
          </nav>
        </div>
        <div className="nucleos-panel__title">
          <i style={{ background: region.color_hex }} />
          <div>
            <h2>{view === "nucleos" ? "Núcleos" : "Cidades"} · {region.name}</h2>
            <span>
              {region.current_responsible_name} ·{" "}
              {view === "nucleos"
                ? `${count} núcleo${count === 1 ? "" : "s"}`
                : `${count} cidade${count === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>
      </div>

      {view === "nucleos" ? (
        nucleos.length === 0 ? (
          <p className="nucleos-panel__empty">
            {searching ? "Nenhum núcleo encontrado para a busca." : "Nenhum núcleo nesta região."}
          </p>
        ) : (
          <div className="nucleos-list">
            {groupedNucleos.map(([city, items]) => (
              <section className="nucleos-city" key={city}>
                <h3>
                  <MapPin size={14} />
                  {city}
                </h3>
                {items.map((nucleo) => (
                  <article className="nucleo-card" key={nucleo.id}>
                    <div className="nucleo-card__main">
                      <strong>{nucleo.nome}</strong>
                      <div className="nucleo-card__meta">
                        {nucleo.endereco ? <span>{nucleo.endereco}{nucleo.bairro ? ` · ${nucleo.bairro}` : ""}</span> : null}
                        {nucleo.presidente_nome ? <span>Presidente: {nucleo.presidente_nome}</span> : null}
                        {nucleo.secretario_nome ? <span>Secretário: {nucleo.secretario_nome}</span> : null}
                        {nucleo.mandato ? <span>Mandato: {nucleo.mandato}</span> : null}
                        <span className="nucleo-card__contacts">
                          {nucleo.telefone ? (
                            <span><Phone size={12} /> {nucleo.telefone}</span>
                          ) : null}
                          {nucleo.email ? (
                            <span><Mail size={12} /> {nucleo.email}</span>
                          ) : null}
                        </span>
                      </div>
                    </div>
                    {canEdit ? (
                      <button className="nucleo-card__edit" type="button" onClick={() => onEdit(nucleo)}>
                        <Pencil size={14} />
                        Editar
                      </button>
                    ) : null}
                  </article>
                ))}
              </section>
            ))}
          </div>
        )
      ) : cities.length === 0 ? (
        <p className="nucleos-panel__empty">
          {searching ? "Nenhuma cidade encontrada para a busca." : "Nenhuma cidade nesta região."}
        </p>
      ) : (
        <div className="nucleos-list">
          {groupedCities.map(([uf, items]) => (
            <section className="state-section" key={uf}>
              <div className="state-heading">
                <div>
                  <h2>{uf}</h2>
                  <span>{items[0]?.state_name}</span>
                </div>
                <strong>{items.length.toLocaleString("pt-BR")}</strong>
              </div>
              <div className="city-table">
                {items.map((city) => (
                  <div className="city-row city-row--single" key={city.ibge_code}>
                    <div className="city-name">
                      <MapPin size={16} />
                      <span>{city.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

const NUCLEO_FIELDS: Array<{ key: keyof Nucleo; label: string; group?: string }> = [
  { key: "nome", label: "Nome do núcleo" },
  { key: "cidade", label: "Cidade" },
  { key: "uf", label: "UF" },
  { key: "endereco", label: "Endereço" },
  { key: "bairro", label: "Bairro" },
  { key: "cep", label: "CEP" },
  { key: "telefone", label: "Telefone" },
  { key: "email", label: "E-mail do núcleo" },
  { key: "presidente_nome", label: "Presidente", group: "Presidente" },
  { key: "presidente_email", label: "E-mail do presidente" },
  { key: "presidente_celular", label: "Celular do presidente" },
  { key: "secretario_nome", label: "Secretário", group: "Secretário" },
  { key: "secretario_email", label: "E-mail do secretário" },
  { key: "secretario_celular", label: "Celular do secretário" },
  { key: "divulgacao_nome", label: "Divulgação", group: "Divulgação" },
  { key: "divulgacao_email", label: "E-mail da divulgação" },
  { key: "divulgacao_celular", label: "Celular da divulgação" },
  { key: "mandato", label: "Mandato (ex.: 2023/2025)", group: "Outros" },
];

function NucleoEditModal({
  nucleo,
  municipalities,
  onClose,
  onSave,
}: {
  nucleo: Nucleo;
  municipalities: Municipality[];
  onClose: () => void;
  onSave: (changes: Partial<Nucleo>) => void | Promise<void>;
}) {
  const ufOptions = React.useMemo(
    () => Array.from(new Set(municipalities.map((m) => m.uf))).sort(),
    [municipalities],
  );
  const citiesByUf = React.useMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of municipalities) {
      const list = map.get(m.uf) || [];
      list.push(m.name);
      map.set(m.uf, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.localeCompare(b, "pt-BR"));
    return map;
  }, [municipalities]);

  const [form, setForm] = React.useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of NUCLEO_FIELDS) initial[field.key] = (nucleo[field.key] as string | null) ?? "";
    // Casa a cidade do nucleo com o nome oficial IBGE (tolerante a acento) para
    // o dropdown ja vir selecionado e salvar sempre o nome canonico.
    const canonical = (citiesByUf.get(initial.uf) || []).find(
      (name) => normalize(name) === normalize(initial.cidade),
    );
    if (canonical) initial.cidade = canonical;
    return initial;
  });
  const [saving, setSaving] = React.useState(false);

  const cityOptions = React.useMemo(() => {
    const list = citiesByUf.get(form.uf) || [];
    // Mantem a cidade atual visivel mesmo se nao casar com a malha (dado legado).
    if (form.cidade && !list.includes(form.cidade)) return [form.cidade, ...list];
    return list;
  }, [citiesByUf, form.uf, form.cidade]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const changes: Partial<Nucleo> = {};
    for (const field of NUCLEO_FIELDS) {
      const next = form[field.key].trim();
      const prev = (nucleo[field.key] as string | null) ?? "";
      if (next !== prev) {
        (changes as Record<string, string | null>)[field.key] = next === "" ? null : next;
      }
    }
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    await onSave(changes);
    setSaving(false);
  }

  return (
    <div className="report-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="report-modal nucleo-modal">
        <header className="report-modal__header">
          <div>
            <h2>Editar núcleo</h2>
            <p>{nucleo.cidade}/{nucleo.uf} · região da ABCCC: {nucleo.abccc_regiao_nome || "—"}</p>
          </div>
          <button className="report-modal__close" onClick={onClose} type="button">
            <XCircle size={22} />
          </button>
        </header>

        <form className="nucleo-form" onSubmit={handleSubmit}>
          <div className="nucleo-form__grid">
            {NUCLEO_FIELDS.map((field) => (
              <React.Fragment key={field.key}>
                {field.group ? <div className="nucleo-form__section">{field.group}</div> : null}
                <label className={field.key === "uf" ? "nucleo-form__field nucleo-form__field--sm" : "nucleo-form__field"}>
                  <span>{field.label}</span>
                  {field.key === "uf" ? (
                    <select
                      value={form.uf}
                      onChange={(e) => {
                        const uf = e.target.value;
                        setForm((prev) => {
                          const valid = (citiesByUf.get(uf) || []).includes(prev.cidade);
                          return { ...prev, uf, cidade: valid ? prev.cidade : "" };
                        });
                      }}
                    >
                      <option value="">—</option>
                      {ufOptions.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </select>
                  ) : field.key === "cidade" ? (
                    <select
                      value={form.cidade}
                      disabled={!form.uf}
                      onChange={(e) => setForm((prev) => ({ ...prev, cidade: e.target.value }))}
                    >
                      <option value="">{form.uf ? "Selecione a cidade…" : "Escolha a UF primeiro"}</option>
                      {cityOptions.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={form[field.key]}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  )}
                </label>
              </React.Fragment>
            ))}
          </div>

          <footer className="report-modal__footer">
            <span className="report-modal__summary">A região comercial segue a cidade do núcleo.</span>
            <div className="report-modal__actions">
              <button className="report-modal__cancel" type="button" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button className="report-modal__generate" type="submit" disabled={saving}>
                <Check size={16} />
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}

function GeographicRegionFilter({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (regions: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!dropdownRef.current?.contains(event.target as Node)) setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function toggleRegion(region: string) {
    if (selected.includes(region)) {
      onChange(selected.filter((item) => item !== region));
      return;
    }
    onChange(GEOGRAPHIC_REGION_OPTIONS.filter((item) => selected.includes(item) || item === region));
  }

  function toggleAll() {
    onChange(selected.length === GEOGRAPHIC_REGION_OPTIONS.length ? [] : GEOGRAPHIC_REGION_OPTIONS);
  }

  return (
    <div className="region-filter" ref={dropdownRef}>
      <button
        className="region-filter__button"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Filter size={18} />
        <span>{formatGeographicRegionFilterLabel(selected)}</span>
        <ChevronDown size={16} />
      </button>

      {open ? (
        <div className="region-filter__panel" role="menu">
          <label className="region-filter__option">
            <input
              type="checkbox"
              checked={selected.length === GEOGRAPHIC_REGION_OPTIONS.length}
              onChange={toggleAll}
            />
            <span className="region-filter__checkbox">{selected.length === GEOGRAPHIC_REGION_OPTIONS.length ? <Check size={14} /> : null}</span>
            <span>Todas</span>
          </label>
          {GEOGRAPHIC_REGION_OPTIONS.map((item) => {
            const checked = selected.includes(item);
            return (
              <label className="region-filter__option" key={item}>
                <input type="checkbox" checked={checked} onChange={() => toggleRegion(item)} />
                <span className="region-filter__checkbox">{checked ? <Check size={14} /> : null}</span>
                <span>{item}</span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function LoginScreen({ error }: { error: string | null }) {
  const [showDev, setShowDev] = React.useState(false);

  React.useEffect(() => {
    setShowDev(location.hostname === "localhost" || location.hostname === "127.0.0.1");
  }, []);

  return (
    <div className="login-screen">
      <div className="login-particles" />
      <section className="login-card">
        <div className="login-card-glow" />
        <h1>Regiões comerciais</h1>
        <p>Faca login com sua conta Google autorizada</p>
        <a href="/api/auth/login" className="google-btn">
          <GoogleIcon />
          Entrar com Google
        </a>
        {showDev ? (
          <a href="/api/auth/dev-login" className="dev-btn">
            Dev Login
          </a>
        ) : null}
        {error ? <div className="login-error">{error}</div> : null}
      </section>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function groupByState(items: Municipality[]): Array<[string, Municipality[]]> {
  const map = new Map<string, Municipality[]>();
  for (const item of items) {
    const list = map.get(item.uf) || [];
    list.push(item);
    map.set(item.uf, list);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, "pt-BR"));
}

function countByRegion(items: Municipality[], regions: Region[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const item of regions) counts[item.id] = 0;
  for (const item of items) counts[item.region_id] = (counts[item.region_id] || 0) + 1;
  return counts;
}

function formatRegionName(region?: Region): string {
  if (!region) return "";
  return `${region.name} (${region.current_responsible_name})`;
}

function formatGeographicRegionFilterLabel(selected: string[]): string {
  if (selected.length === GEOGRAPHIC_REGION_OPTIONS.length) return "Todas";
  if (selected.length === 1) return selected[0];
  return `${selected.length} regiões`;
}

function readStoredGeographicRegions(): string[] {
  try {
    const raw = window.localStorage.getItem(GEOGRAPHIC_REGION_FILTER_STORAGE_KEY);
    if (!raw) return GEOGRAPHIC_REGION_OPTIONS;

    const stored = JSON.parse(raw) as Partial<StoredGeographicRegionFilter>;
    if (!Array.isArray(stored.value) || typeof stored.expiresAt !== "number" || stored.expiresAt <= Date.now()) {
      window.localStorage.removeItem(GEOGRAPHIC_REGION_FILTER_STORAGE_KEY);
      return GEOGRAPHIC_REGION_OPTIONS;
    }

    const valid = GEOGRAPHIC_REGION_OPTIONS.filter((item) => stored.value?.includes(item));
    return valid.length === stored.value.length ? valid : GEOGRAPHIC_REGION_OPTIONS;
  } catch {
    return GEOGRAPHIC_REGION_OPTIONS;
  }
}

function storeGeographicRegions(value: string[]) {
  try {
    const payload: StoredGeographicRegionFilter = {
      value,
      expiresAt: Date.now() + GEOGRAPHIC_REGION_FILTER_TTL_MS,
    };
    window.localStorage.setItem(GEOGRAPHIC_REGION_FILTER_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures so private browsing or quota issues never break filtering.
  }
}

// Lista legivel de regioes na ordem oficial: "Norte, Nordeste e Sul".
function formatGeographicRegions(selected: string[]): string {
  const ordered = GEOGRAPHIC_REGION_OPTIONS.filter((item) => selected.includes(item));
  if (ordered.length === 0) return "nenhuma região";
  if (ordered.length === 1) return ordered[0];
  return `${ordered.slice(0, -1).join(", ")} e ${ordered[ordered.length - 1]}`;
}

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getLoginError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  if (!error) return null;
  window.history.replaceState({}, "", "/");
  return ERROR_MESSAGES[error] || "Erro desconhecido.";
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
